import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';

import { DefaultFilledButton, PrimaryButton } from '@magoosh/lib/button';
import FieldInput from '@magoosh/lib/formik_inputs/field_input';
import { Form } from '@magoosh/lib/forms';

import * as api from '@admin/app/api';

import DraftPromptPreview from '../draft_prompt_preview';
import { DraftPrompt } from '../../types';

interface Props {
  draftPromptId: number;
  onClose: () => void;
  onSuccess: (draftPrompt: DraftPrompt) => void;
}

const RequestContentFixModal: React.FC<Props> = (props) => {
  const poller = useRef(null);
  const [history, setHistory] = useState([]);
  const [isWorking, setIsWorking] = useState(false);
  const [fix, setFix] = useState(null);

  useEffect(() => {
    if (!fix) return;
    if (isDone(fix) || isErrored(fix)) return;

    poller.current = setTimeout(pollForUpdates, 1000);

    return () => {
      clearTimeout(poller.current);
    }
  }, [fix]);

  const accept = () => {
    api.acceptTestPrepAIContentFix(fix.id).then((fix) => {
      props.onSuccess({
        ...fix.draftContent,
        id: props.draftPromptId,
        resultingPrompt: null
      });
      props.onClose();
    });
  };

  const handleOnSubmit = (values, { setErrors, setFieldValue }) => {
    setIsWorking(true);

    if (fix) {
      api.reopenTestPrepAIContentFix(fix.id, values.instructions).then((fix) => {
        setHistory([...history, values.instructions]);
        setFieldValue('instructions', '')
        setFix(fix);
      }).catch((error) => {
        setErrors(error.errors);
        setIsWorking(false);
      });
    } else {
      const contentFix = {
        contentId: props.draftPromptId,
        contentType: 'TestPrep::AI::DraftPrompt',
        context: {
          instructionsInputs: [values.instructions]
        }
      };

      api.createTestPrepAIContentFix(contentFix).then((fix) => {
        setHistory([...history, values.instructions]);
        setFieldValue('instructions', '')
        setFix(fix);
      }).catch((error) => {
        setErrors(error.errors);
        setIsWorking(false);
      });
    }
  };

  const isDone = (contentFix) => {
    return ['pending', 'accepted', 'rejected'].indexOf(contentFix.status) !== -1;
  };

  const isErrored = (contentFix) => {
    return contentFix.status === 'errored';
  };

  const pollForUpdates = () => {
    api.fetchTestPrepAIContentFix(fix.id).then((contentFix) => {
      setFix(contentFix);
      if (isDone(contentFix)) {
        setIsWorking(false);
        return;
      } else if (isErrored(contentFix)) {
        setIsWorking(false);
        return;
      }

      poller.current = setTimeout(pollForUpdates, 2000);
    });
  };

  const initialValues = {
    instructions: ''
  };

  return (
    <div className="d-flex flex-column gap-m">
      <div>
        {
          fix && (
            isWorking ? (
              <div className="well well-hollow">Working...</div>
            ) : (
              <DraftPromptPreview
                draftPrompt={fix.draftContent}
                isEditable={false}
              />
            )
          )
        }
      </div>

      <div className="d-flex flex-column gap-s u-margin-T-s">
        {
          history.map((item, idx) => (
            <div key={idx} className="d-flex gap-s">
              <div className="flex-1">
                {'>'} {item}
              </div>
            </div>
          ))
        }
      </div>

      <Formik initialValues={initialValues} onSubmit={handleOnSubmit}>
        {() => {
          return (
            <Form>
              <FieldInput name="instructions" style={{ height: 200 }} type="textarea" />

              <div className="d-flex gap-s">
                <div className="flex-1">
                  <DefaultFilledButton type="submit" submitting={isWorking}>Request</DefaultFilledButton>
                </div>

                <div>
                  <PrimaryButton
                    disabled={!fix || !isDone(fix)}
                    onClick={accept}
                    submitting={isWorking}
                  >
                    Accept
                  </PrimaryButton>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default RequestContentFixModal;
